import { useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { UnregisterCallback } from 'history';
import i18next from 'i18next';

/**
 *
 * @param attachDialog whether to show the prompt or not
 * @param message custom message to display when the prompt shows up
 * @description prevents user from leaving a page without fulling a condition
 */
export const usePrompt = (attachDialog: boolean, message: string = i18next.t('unSavedInformation')) => {
  const history = useHistory();

  const self = useRef<UnregisterCallback | null>();

  const onWindowOrTabClose = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!attachDialog) {
        return;
      }

      if (typeof event === 'undefined') {
        event = window.event as BeforeUnloadEvent;
      }

      if (event) {
        event.returnValue = message;
      }

      return message;
    },
    [message, attachDialog]
  );

  useEffect(() => {
    if (attachDialog) {
      self.current = history.block(message);
    } else {
      self.current = null;
    }

    window.addEventListener('beforeunload', onWindowOrTabClose);

    return () => {
      if (self.current) {
        self.current();
        self.current = null;
      }

      window.removeEventListener('beforeunload', onWindowOrTabClose);
    };
  }, [message, attachDialog, onWindowOrTabClose, history]);
};

export function transformArrayToDictionary<T>(array: T[], keyUsed: keyof T): { [key: string]: T };

export function transformArrayToDictionary<T, ValueTransformed>(
  array: T[],
  keyUsed: keyof T,
  map: (arrayValue: T) => ValueTransformed
): { [key: string]: ValueTransformed };

export function transformArrayToDictionary<T, ValueTransformed>(
  array: T[],
  keyUsed: keyof T,
  map?: (arrayValue: T) => ValueTransformed
): { [key: string]: T } {
  return array.reduce((acc, current: T) => {
    const key = `${current[keyUsed]}`.toString();
    acc[key] = map ? map(current) : current;

    return acc;
  }, {});
}
